import React, { useEffect, useState } from 'react';
import { adminGetContentByType, adminCreateContent, adminUpdateContent, adminListMedia } from '../../api/adminApi';
import { ContentDocument, LegalArea } from '../../api/contentApi';

const LANGS = ['de', 'fr', 'en'] as const;
type Lang = (typeof LANGS)[number];
const LANG_LABELS: Record<Lang, string> = { de: 'Deutsch', fr: 'Français', en: 'English' };

interface MediaItem {
  name: string;
  url: string;
  contentType: string;
  size: number;
}

interface EmailSettings {
  emailNotificationAddress: string;
  emailSenderName: string;
  emailNotificationAttachmentUrls: string[];
  emailConfirmationAttachmentUrls: string[];
}

const emptyEmailSettings = (): EmailSettings => ({
  emailNotificationAddress: '',
  emailSenderName: '',
  emailNotificationAttachmentUrls: [],
  emailConfirmationAttachmentUrls: [],
});

interface MediaAttachmentPickerProps {
  label: string;
  description?: string;
  mediaItems: MediaItem[];
  selected: string[];
  onChange: (urls: string[]) => void;
}

const MediaAttachmentPicker: React.FC<MediaAttachmentPickerProps> = ({
  label, description, mediaItems, selected, onChange,
}) => {
  const toggle = (url: string) => {
    if (selected.includes(url)) {
      onChange(selected.filter((u) => u !== url));
    } else {
      onChange([...selected, url]);
    }
  };

  const selectedItems = selected.map((url) => mediaItems.find((m) => m.url === url) ?? { name: url, url, contentType: '', size: 0 });

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {description && <p className="text-xs text-gray-400 mb-2">{description}</p>}

      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedItems.map((item) => (
            <span
              key={item.url}
              className="inline-flex items-center gap-1.5 bg-primary-50 border border-primary-200 text-primary-700 text-xs px-2 py-1 rounded-md"
            >
              <span className="max-w-[160px] truncate" title={item.name}>{item.name}</span>
              <button
                onClick={() => toggle(item.url)}
                className="text-primary-400 hover:text-primary-700 leading-none"
                title="Entfernen"
              >✕</button>
            </span>
          ))}
        </div>
      )}

      {mediaItems.length === 0 ? (
        <p className="text-xs text-gray-400 italic">Keine Medien verfügbar — bitte zuerst Dateien hochladen.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
            {mediaItems.map((item) => {
              const isSelected = selected.includes(item.url);
              return (
                <label
                  key={item.name}
                  className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm transition ${
                    isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(item.url)}
                    className="accent-primary-600 shrink-0"
                  />
                  <span className="flex-1 truncate text-gray-700" title={item.name}>{item.name}</span>
                  <span className="text-xs text-gray-400 shrink-0">
                    {item.contentType === 'application/pdf' ? 'PDF'
                      : item.contentType.startsWith('image/') ? 'IMG'
                      : item.contentType.includes('word') ? 'DOC'
                      : 'FILE'}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface UrlSettings {
  urlPrivacy: string;
  urlTerms: string;
  urlCancellation: string;
}

const emptyUrlSettings = (): UrlSettings => ({ urlPrivacy: '', urlTerms: '', urlCancellation: '' });

const emptyArea = (): LegalArea => ({ id: '', de: '', fr: '', en: '' });

/**
 * Dedicated settings page — manages legal-area options and document/legal URLs.
 * Replaces the generic AdminContentEditor for the "settings" content type.
 */
const AdminSettings: React.FC = () => {
  const [doc, setDoc] = useState<ContentDocument | null>(null);
  const [loading, setLoading] = useState(true);

  // Legal areas state
  const [areas, setAreas] = useState<LegalArea[]>([]);
  const [areaError, setAreaError] = useState('');
  const [areaSaving, setAreaSaving] = useState(false);
  const [areaSaved, setAreaSaved] = useState(false);

  // URL settings state — per language
  const [urls, setUrls] = useState<Record<Lang, UrlSettings>>(
    Object.fromEntries(LANGS.map((l) => [l, emptyUrlSettings()])) as Record<Lang, UrlSettings>
  );
  const [urlActiveLang, setUrlActiveLang] = useState<Lang>('de');
  const [urlSaving, setUrlSaving] = useState(false);
  const [urlSaved, setUrlSaved] = useState(false);
  const [urlError, setUrlError] = useState('');

  // Email settings state
  const [emailConfig, setEmailConfig] = useState<EmailSettings>(emptyEmailSettings());
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [docs, media] = await Promise.all([
          adminGetContentByType('settings'),
          adminListMedia().catch(() => [] as MediaItem[]),
        ]);
        setMediaItems(media);

        const existing = docs[0] ?? null;
        setDoc(existing);

        if (existing) {
          setAreas(existing.legalAreas ?? []);
          // Read per-language URL settings from each translation
          setUrls(
            Object.fromEntries(
              LANGS.map((lang) => [
                lang,
                {
                  urlPrivacy:     existing.translations?.[lang]?.urlPrivacy     ?? '',
                  urlTerms:       existing.translations?.[lang]?.urlTerms       ?? '',
                  urlCancellation: existing.translations?.[lang]?.urlCancellation ?? '',
                },
              ])
            ) as Record<Lang, UrlSettings>
          );
          setEmailConfig({
            emailNotificationAddress: existing.emailNotificationAddress ?? '',
            emailSenderName: existing.emailSenderName ?? '',
            emailNotificationAttachmentUrls: existing.emailNotificationAttachmentUrls ?? [],
            emailConfirmationAttachmentUrls: existing.emailConfirmationAttachmentUrls ?? [],
          });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── Legal areas helpers ─────────────────────────────────────────────────────

  const handleAreaChange = (index: number, field: keyof LegalArea, value: string) => {
    setAreaSaved(false);
    setAreas((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  };

  const addArea = () => {
    setAreaSaved(false);
    setAreas((prev) => [...prev, emptyArea()]);
  };

  const removeArea = (index: number) => {
    setAreaSaved(false);
    setAreas((prev) => prev.filter((_, i) => i !== index));
  };

  const moveArea = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= areas.length) return;
    setAreaSaved(false);
    setAreas((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const validateAreas = (): boolean => {
    const ids = areas.map((a) => a.id.trim());
    if (ids.some((id) => !id)) {
      setAreaError('Alle Rechtsgebiete brauchen eine ID.');
      return false;
    }
    if (new Set(ids).size !== ids.length) {
      setAreaError('IDs müssen eindeutig sein.');
      return false;
    }
    setAreaError('');
    return true;
  };

  const saveAreas = async () => {
    if (!validateAreas()) return;
    setAreaSaving(true);
    setAreaError('');
    setAreaSaved(false);
    try {
      const updated = await upsertDoc({ legalAreas: areas.map((a) => ({ ...a, id: a.id.trim() })) });
      setDoc(updated);
      setAreaSaved(true);
    } catch (err: any) {
      setAreaError(`Fehler: ${err.message}`);
    } finally {
      setAreaSaving(false);
    }
  };

  // ── URL helpers ─────────────────────────────────────────────────────────────

  const saveUrls = async () => {
    setUrlSaving(true);
    setUrlError('');
    setUrlSaved(false);
    try {
      const existingTranslations = doc?.translations ?? {};
      const mergedTranslations = Object.fromEntries(
        LANGS.map((lang) => [
          lang,
          {
            ...(existingTranslations[lang] ?? { isPublished: true }),
            urlPrivacy:      urls[lang].urlPrivacy,
            urlTerms:        urls[lang].urlTerms,
            urlCancellation: urls[lang].urlCancellation,
          },
        ])
      );
      const updated = await upsertDoc({ translations: mergedTranslations });
      setDoc(updated);
      setUrlSaved(true);
    } catch (err: any) {
      setUrlError(`Fehler: ${err.message}`);
    } finally {
      setUrlSaving(false);
    }
  };

  // ── Email settings helpers ───────────────────────────────────────────────────

  const saveEmailConfig = async () => {
    setEmailSaving(true);
    setEmailError('');
    setEmailSaved(false);
    try {
      const updated = await upsertDoc({
        emailNotificationAddress: emailConfig.emailNotificationAddress || undefined,
        emailSenderName: emailConfig.emailSenderName || undefined,
        emailNotificationAttachmentUrls: emailConfig.emailNotificationAttachmentUrls,
        emailConfirmationAttachmentUrls: emailConfig.emailConfirmationAttachmentUrls,
      });
      setDoc(updated);
      setEmailSaved(true);
    } catch (err: any) {
      setEmailError(`Fehler: ${err.message}`);
    } finally {
      setEmailSaving(false);
    }
  };

  // ── Shared upsert ────────────────────────────────────────────────────────────

  const upsertDoc = async (patch: Partial<ContentDocument>): Promise<ContentDocument> => {
    if (doc) {
      return adminUpdateContent('settings', doc.id, { ...doc, ...patch });
    } else {
      const created = await adminCreateContent({
        type: 'settings',
        slug: 'settings',
        isPublished: true,
        sortOrder: 0,
        translations: {},
        ...patch,
      });
      return created;
    }
  };

  // ────────────────────────────────────────────────────────────────────────────

  if (loading) return <p className="text-gray-500">Laden…</p>;

  return (
    <div className="max-w-2xl space-y-10">
      <h1 className="text-2xl font-bold text-gray-800">Einstellungen</h1>

      {/* ── Legal areas ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-700 mb-1">Rechtsgebiete</h2>
        <p className="text-sm text-gray-500 mb-4">
          Diese Liste erscheint im Kontaktformular als Dropdown. Die <strong>ID</strong> (Kleinbuchstaben,
          kein Leerzeichen) wird als Schlüssel für E-Mail-Vorlagen verwendet.
        </p>

        <div className="space-y-3">
          {areas.map((area, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs text-gray-400 font-mono w-4">{i + 1}</span>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">ID (Slug)</label>
                  <input
                    type="text"
                    value={area.id}
                    onChange={(e) => handleAreaChange(i, 'id', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                    placeholder="z.B. familienrecht"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm font-mono focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => moveArea(i, -1)}
                    disabled={i === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-20 text-xs leading-none"
                    title="Nach oben"
                  >▲</button>
                  <button
                    onClick={() => moveArea(i, 1)}
                    disabled={i === areas.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-20 text-xs leading-none"
                    title="Nach unten"
                  >▼</button>
                </div>
                <button
                  onClick={() => removeArea(i)}
                  className="text-red-400 hover:text-red-600 text-sm shrink-0"
                  title="Entfernen"
                >✕</button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {LANGS.map((lang) => (
                  <div key={lang}>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      {LANG_LABELS[lang]}
                    </label>
                    <input
                      type="text"
                      value={(area[lang] as string) ?? ''}
                      onChange={(e) => handleAreaChange(i, lang, e.target.value)}
                      placeholder={`Bezeichnung ${lang}`}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <button
            onClick={addArea}
            className="w-full border-2 border-dashed border-gray-300 hover:border-primary-400 text-gray-500 hover:text-primary-600 rounded-lg py-2.5 text-sm transition"
          >
            + Rechtsgebiet hinzufügen
          </button>
        </div>

        {areaError && <p className="text-sm text-red-600 mt-3">{areaError}</p>}
        {areaSaved && <p className="text-sm text-green-600 mt-3">✓ Gespeichert</p>}

        <button
          onClick={saveAreas}
          disabled={areaSaving}
          className="mt-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition disabled:opacity-50"
        >
          {areaSaving ? 'Speichern…' : 'Rechtsgebiete speichern'}
        </button>
      </section>

      <hr className="border-gray-200" />

      {/* ── Document URLs ─────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-700">Rechtliche Dokumente</h2>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden text-sm">
            {LANGS.map((lang) => (
              <button
                key={lang}
                onClick={() => setUrlActiveLang(lang)}
                className={`px-4 py-1.5 transition ${
                  urlActiveLang === lang
                    ? 'bg-primary-600 text-white font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {LANG_LABELS[lang]}
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          URLs zu Datenschutzerklärung, AGB und Widerrufsrecht — erscheinen im Kontaktformular.
        </p>

        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          {([
            { key: 'urlPrivacy', label: 'Datenschutzerklärung URL' },
            { key: 'urlTerms', label: 'AGB URL' },
            { key: 'urlCancellation', label: 'Widerrufsrecht URL' },
          ] as const).map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <input
                type="url"
                value={urls[urlActiveLang][key]}
                onChange={(e) => {
                  setUrlSaved(false);
                  setUrls((prev) => ({
                    ...prev,
                    [urlActiveLang]: { ...prev[urlActiveLang], [key]: e.target.value },
                  }));
                }}
                placeholder="https://…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
          ))}
        </div>

        {urlError && <p className="text-sm text-red-600 mt-3">{urlError}</p>}
        {urlSaved && <p className="text-sm text-green-600 mt-3">✓ Gespeichert</p>}

        <button
          onClick={saveUrls}
          disabled={urlSaving}
          className="mt-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition disabled:opacity-50"
        >
          {urlSaving ? 'Speichern…' : 'URLs speichern'}
        </button>
      </section>

      <hr className="border-gray-200" />

      {/* ── Email settings ─────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold text-gray-700 mb-1">E-Mail Einstellungen</h2>
        <p className="text-sm text-gray-500 mb-4">
          Absendername und Empfängeradresse für ausgehende E-Mails. Überschreiben die Konfigurationswerte.
          Anhänge werden automatisch an jede E-Mail angehängt.
        </p>

        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Absender-Name</label>
            <input
              type="text"
              value={emailConfig.emailSenderName}
              onChange={(e) => { setEmailSaved(false); setEmailConfig((p) => ({ ...p, emailSenderName: e.target.value })); }}
              placeholder="z.B. Clairmont Law"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Benachrichtigungs-E-Mail (Kanzlei)</label>
            <input
              type="email"
              value={emailConfig.emailNotificationAddress}
              onChange={(e) => { setEmailSaved(false); setEmailConfig((p) => ({ ...p, emailNotificationAddress: e.target.value })); }}
              placeholder="anfragen@ihre-kanzlei.de"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">Leer lassen um den Konfigurationswert (Contact:NotificationEmail) zu verwenden.</p>
          </div>
        </div>

        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4 space-y-5">
          <MediaAttachmentPicker
            label="Anhänge Benachrichtigung (an Kanzlei)"
            description="Dateien die automatisch an die Eingangs-Benachrichtigung angehängt werden."
            mediaItems={mediaItems}
            selected={emailConfig.emailNotificationAttachmentUrls}
            onChange={(urls) => { setEmailSaved(false); setEmailConfig((p) => ({ ...p, emailNotificationAttachmentUrls: urls })); }}
          />
          <MediaAttachmentPicker
            label="Anhänge Bestätigung (an Mandant, z.B. AGB)"
            description="Dateien die automatisch an die Bestätigungs-E-Mail an den Mandanten angehängt werden."
            mediaItems={mediaItems}
            selected={emailConfig.emailConfirmationAttachmentUrls}
            onChange={(urls) => { setEmailSaved(false); setEmailConfig((p) => ({ ...p, emailConfirmationAttachmentUrls: urls })); }}
          />
        </div>

        {emailError && <p className="text-sm text-red-600 mt-3">{emailError}</p>}
        {emailSaved && <p className="text-sm text-green-600 mt-3">✓ Gespeichert</p>}

        <button
          onClick={saveEmailConfig}
          disabled={emailSaving}
          className="mt-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-6 py-2 rounded-lg transition disabled:opacity-50"
        >
          {emailSaving ? 'Speichern…' : 'E-Mail Einstellungen speichern'}
        </button>
      </section>
    </div>
  );
};

export default AdminSettings;
